/*
 * Yadid Advisory shared PDF pipeline.
 *
 * The Meeting Brief PDF (Iter 85.17–85.28) is the reference implementation.
 * This module extracts the parts that are firm-level standards, not
 * memo-specific content:
 *
 *   · Letter-size print margins with a 22mm top/bottom safe zone.
 *   · Page-break-avoid pipeline (headings, paragraphs, list items,
 *     blockquotes, plus caller-supplied selectors).
 *   · Running letterhead wordmark on pages 2+ (brass hairline underneath).
 *   · Running "PRIVILEGED & CONFIDENTIAL · Page X of N · © YEAR YADID
 *     ADVISORY" footer on every page (brass hairline above).
 *   · Anchor points at the shared document column (DOC_INSET_MM) so
 *     running elements line up with the memo body content edge.
 *   · Compensation for jsPDF's `align:"right"` ignoring `setCharSpace`
 *     so the right-aligned copyright lands exactly at pageW − DOC_INSET.
 *
 * Callers are responsible for constructing the offscreen DOM they want
 * to rasterize (masthead, body, in-content footer if any) and passing
 * the root element in. This module wraps html2pdf, applies the shared
 * options, and layers the running header/footer via a jsPDF
 * post-processing pass.
 *
 * Usage:
 *   await renderYadidPdf({
 *     element: offscreenNode,
 *     filename: "meeting_brief.pdf",
 *     extraPagebreakAvoid: [".keep-with-next", ".masthead", ".meta"],
 *   });
 */

import {
  YADID_PAGE_FORMAT,
  YADID_PDF_MARGIN_MM,
  YADID_DOC_INSET_MM,
  YADID_BRASS_STROKE_MM,
  YADID_RUNNING_HEADER_TOP_MM,
  YADID_RUNNING_HEADER_RULE_MM,
  YADID_RUNNING_FOOTER_RULE_MM,
  YADID_RUNNING_FOOTER_TEXT_MM,
  YADID_FIRM_WORDMARK,
  YADID_CONFIDENTIAL_LABEL,
  YADID_COPYRIGHT_FN,
  YADID_PDF_WINDOW_WIDTH,
  YADID_NAVY,
  YADID_BRASS,
  YADID_MUTED,
} from "./tokens";

const hexToRgbTriplet = (hex) => {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

const DEFAULT_PAGEBREAK_AVOID = ["h1", "h2", "h3", "p", "li", "blockquote"];

export async function renderYadidPdf({
  element,
  filename,
  extraPagebreakAvoid = [],
  charSpaceEm = 0.5,
  showRunningHeaderOnPageOne = false,
  scale = 2,
} = {}) {
  if (!element) throw new Error("renderYadidPdf: element is required");
  if (!filename) throw new Error("renderYadidPdf: filename is required");
  const { default: html2pdf } = await import("html2pdf.js");
  const avoid = Array.from(new Set([...DEFAULT_PAGEBREAK_AVOID, ...extraPagebreakAvoid]));
  const navy = hexToRgbTriplet(YADID_NAVY);
  const brass = hexToRgbTriplet(YADID_BRASS);
  const muted = hexToRgbTriplet(YADID_MUTED);
  const year = new Date().getFullYear();
  const copyright = YADID_COPYRIGHT_FN(year);
  const copyOverflow = charSpaceEm * Math.max(0, copyright.length - 1);
  await html2pdf()
    .set({
      filename,
      margin: YADID_PDF_MARGIN_MM,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        scale,
        backgroundColor: "#ffffff",
        useCORS: true,
        allowTaint: false,
        windowWidth: YADID_PDF_WINDOW_WIDTH,
        logging: false,
      },
      jsPDF: { unit: "mm", format: YADID_PAGE_FORMAT, orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid },
    })
    .from(element)
    .toPdf()
    .get("pdf")
    .then((pdf) => {
      const total = pdf.internal.getNumberOfPages();
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        // Running letterhead wordmark — pages 2+ (page 1 typically
        // carries a richer in-content masthead so we don't duplicate).
        if (i > 1 || showRunningHeaderOnPageOne) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7);
          pdf.setTextColor(navy[0], navy[1], navy[2]);
          pdf.setCharSpace(charSpaceEm);
          pdf.text(YADID_FIRM_WORDMARK, YADID_DOC_INSET_MM, YADID_RUNNING_HEADER_TOP_MM);
          pdf.setCharSpace(0);
          pdf.setDrawColor(brass[0], brass[1], brass[2]);
          pdf.setLineWidth(YADID_BRASS_STROKE_MM);
          pdf.line(
            YADID_DOC_INSET_MM,
            YADID_RUNNING_HEADER_RULE_MM,
            pageW - YADID_DOC_INSET_MM,
            YADID_RUNNING_HEADER_RULE_MM,
          );
        }
        // Running footer — every page.
        pdf.setDrawColor(brass[0], brass[1], brass[2]);
        pdf.setLineWidth(YADID_BRASS_STROKE_MM);
        pdf.line(
          YADID_DOC_INSET_MM,
          pageH - YADID_RUNNING_FOOTER_RULE_MM,
          pageW - YADID_DOC_INSET_MM,
          pageH - YADID_RUNNING_FOOTER_RULE_MM,
        );
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        pdf.setTextColor(navy[0], navy[1], navy[2]);
        pdf.setCharSpace(charSpaceEm);
        pdf.text(YADID_CONFIDENTIAL_LABEL, YADID_DOC_INSET_MM, pageH - YADID_RUNNING_FOOTER_TEXT_MM);
        pdf.setCharSpace(0);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(muted[0], muted[1], muted[2]);
        pdf.text(`Page ${i} of ${total}`, pageW / 2, pageH - YADID_RUNNING_FOOTER_TEXT_MM, {
          align: "center",
        });
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(navy[0], navy[1], navy[2]);
        pdf.setCharSpace(charSpaceEm);
        // jsPDF align:"right" computes text width from getTextWidth()
        // which ignores setCharSpace; compensate so the rendered right
        // edge lands exactly at pageW − DOC_INSET.
        pdf.text(
          copyright,
          pageW - YADID_DOC_INSET_MM - copyOverflow,
          pageH - YADID_RUNNING_FOOTER_TEXT_MM,
          { align: "right" },
        );
        pdf.setCharSpace(0);
      }
    })
    .save();
}
